import {
  MutationCache,
  QueryCache,
  QueryClient,
  type Query,
} from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { captureEvent, sanitizeErrorReason } from "./analytics";
import { GoogleScopeError } from "./errors";

type MutationAnalyticsMeta = {
  successEvent?: string;
  successProps?: Record<string, unknown>;
};

const toKey = (value: unknown) => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return "unknown";
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof GoogleScopeError) {
        return;
      }

      captureEvent("api_query_failed", {
        queryKey: toKey(query.queryKey),
        reason: sanitizeErrorReason(error),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      captureEvent("api_mutation_failed", {
        mutationKey: toKey(mutation.options.mutationKey),
        reason: sanitizeErrorReason(error),
      });
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      const analytics = ((mutation.options.meta as Record<string, unknown>)?.analytics ||
        {}) as MutationAnalyticsMeta;
      if (!analytics.successEvent) {
        return;
      }

      captureEvent("api_mutation_succeeded", {
        mutationKey: toKey(mutation.options.mutationKey),
        event: analytics.successEvent,
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 1000 * 60 * 5,
    },
  },
});

export const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

// `__APP_VERSION__` is injected by Vite `define`; guard so tests/non-Vite runtimes don't throw.
const appVersion =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

// Persister scoping: bound staleness (1 day), auto-evict the whole cache on each
// release (buster), and only persist SUCCESSFUL, non-volatile queries — so error/
// pending states and high-churn third-party feeds (e.g. weather) don't hit disk.
const VOLATILE_QUERY_PREFIXES = new Set(["weather", "slack-thumb", "slack-feed"]);

export const persistOptions = {
  persister,
  maxAge: 1000 * 60 * 60 * 24,
  buster: appVersion,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) =>
      query.state.status === "success" &&
      !VOLATILE_QUERY_PREFIXES.has(String(query.queryKey[0])),
  },
} as const;
